import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { usePlatformSanctionsOverride } from "@/lib/platform";

/**
 * Set / clear the `sanctions_force_clean_until` override on an org.
 *
 * Future timestamps tell the weekly sanctions sweep "treat this org
 * as clean even when the matcher fires." The matches still land in
 * `sanctions_matches_jsonb` so ops can audit; the customer-visible
 * `sanctions_match_found` boolean stays False until the override
 * expires.
 *
 * The reason field is required as a soft gate — ops should always
 * log WHY they overrode a match (legal sign-off, false positive
 * confirmed against the source, etc.).
 */
export function SanctionsOverrideModal({
  orgId,
  orgName,
  currentUntil,
  onClose,
}: {
  orgId: number | string;
  orgName: string;
  currentUntil: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [until, setUntil] = useState<string>(
    currentUntil ? toLocalDatetime(currentUntil) : "",
  );
  const [reason, setReason] = useState("");
  const mutation = usePlatformSanctionsOverride(orgId);

  return (
    <div
      className="modal-backdrop bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sanctions-override-heading"
    >
      <form
        className="w-full max-w-md rounded-lg border border-nb-border bg-nb-surface p-6 shadow-xl"
        onSubmit={async (e) => {
          e.preventDefault();
          await mutation.mutateAsync({
            until: until ? new Date(until).toISOString() : null,
            reason: reason.trim(),
          });
          onClose();
        }}
      >
        <h2
          id="sanctions-override-heading"
          className="text-lg font-semibold"
        >
          {t("ui.sanctionsOverrideModal.heading", { orgName })}
        </h2>
        <p className="mt-2 text-sm text-nb-mute">
          <Trans i18nKey="ui.sanctionsOverrideModal.intro">
            A future <code>until</code> timestamp tells the weekly sweep
            to ignore matcher hits. Leave blank to clear an existing
            override. Match details stay in the audit log either way.
          </Trans>
        </p>
        <label className="mt-4 block text-sm">
          <span className="text-nb-mute">
            {t("ui.sanctionsOverrideModal.untilLabel")}
          </span>
          <input
            type="datetime-local"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="mt-1 w-full rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
            data-testid="sanctions-override-until"
          />
        </label>
        <label className="mt-3 block text-sm">
          <span className="text-nb-mute">
            {t("ui.sanctionsOverrideModal.reasonLabel")}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
            placeholder={t("ui.sanctionsOverrideModal.reasonPlaceholder")}
            data-testid="sanctions-override-reason"
          />
        </label>
        {mutation.isError && (
          <div className="mt-2 text-sm text-nb-danger" role="alert">
            {t("ui.sanctionsOverrideModal.errorGeneric")}
          </div>
        )}
        <div className="mt-5 flex justify-between gap-2">
          {currentUntil && (
            <button
              type="button"
              className="btn-ghost text-nb-danger"
              disabled={mutation.isPending}
              onClick={async () => {
                await mutation.mutateAsync({
                  until: null,
                  reason: reason.trim() || "cleared",
                });
                onClose();
              }}
              data-testid="sanctions-override-clear"
            >
              {t("ui.sanctionsOverrideModal.clearOverride")}
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>
              {t("ui.sanctionsOverrideModal.cancel")}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={mutation.isPending}
              data-testid="sanctions-override-submit"
            >
              {mutation.isPending ? t("ui.sanctionsOverrideModal.saving") : t("ui.sanctionsOverrideModal.save")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}


/** ISO 8601 from the backend → `YYYY-MM-DDTHH:MM` for the
 *  datetime-local input. Drops the seconds + tz suffix the input
 *  doesn't accept. */
function toLocalDatetime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
